import { useState } from "react";
import { useParams } from "react-router-dom";

import { useRealTimeTypedQuery } from "components/WebSocket/hooks";
import { TYRE_LOCATIONS } from "models";
import { AppointmentDetailsKeys } from "modules/AppointmentDetails/queryKeys";
import { useAppointmentListeners } from "modules/AppointmentDetails/useAppointmentListeners";
import { groupTruckTyres } from "modules/CarDetails/hooks";
import { PATHS } from "router/paths";
import { ApiInstance, RESOURCE_NOT_FOUND } from "utils/Api";
import { updateAppointmentStatusIdentifier } from "utils/appointmentUtils";

import type { TypedBackendQueryKey } from "hooks";
import type { Appointment } from "models";
import type { AppointmentDetailUrlParams } from "router/hooks";

export type AppointmentDetailRequestPayload = { id: number } | { dealer_location_id: number; wo: string };

const getEndpoint = (requestPayload: AppointmentDetailRequestPayload, queryKeyEndpoint: string) => {
  if ("wo" in requestPayload) return PATHS.APPOINTMENT_DETAIL_VIEW_BY_WO;

  return queryKeyEndpoint;
};

// TODO: this is a mess and not wortking properly, to be removed, we do not handle this here, and we do not want a /not-found url, this file is to load the appointment, if there is an error, it's to be handled in the main component of the module, see what's done in car detail page
export const getAppointment = async (queryKey: TypedBackendQueryKey<Appointment>, requestPayload: AppointmentDetailRequestPayload): Promise<Appointment> => {
  try {
    const { baseUrl, params } = queryKey;
    const endpoint = getEndpoint(requestPayload, queryKey.endpoint);

    return (await ApiInstance.post(endpoint, { ...params, ...requestPayload }, baseUrl)).data;
  } catch (error: any) {
    const errorString = error.toString();
    const regex = /"code":(\d+),"data":"([^"]+)"/;
    const match = errorString.match(regex);

    if (match) {
      const code = parseInt(match[1], 10);
      const data = match[2];

      if (code === 404 && data === RESOURCE_NOT_FOUND) {
        if ("id" in requestPayload) window.open(`/appointments/${requestPayload.id}/not-found`, "_self");
        else window.open(`/appointments/open_by_wonr/${requestPayload.dealer_location_id}_${requestPayload.wo}/not-found`, "_self");
      }
    }
  }

  throw new Error();
};

const sortAppointmentProperties = (appointment: Appointment) => {
  // TODO: sort everything properly here instead of on render in components
  //sortedAppointment.interventions = appointment.interventions?.toSorted(...);
  appointment.interventions?.forEach(i => {
    i.pending_checklists?.sort((a, b) => a.ordering - b.ordering);
    i.pin_history?.sort((a, b) => b.id - a.id);
    i.elements?.sort((a, b) => {
      if (!a.dms_nr && !b.dms_nr) return (a.id || 0) - (b.id || 0);

      if (a.parent_dms_nr === b.parent_dms_nr) return a.dms_nr.localeCompare(b.dms_nr, undefined, { numeric: true, sensitivity: "base" });

      if ((a.parent_dms_nr === "") !== (b.parent_dms_nr === "")) {
        if (a.parent_dms_nr === b.dms_nr) return 1;
        if (b.parent_dms_nr === a.dms_nr) return -1;
        return (a.parent_dms_nr || a.dms_nr).localeCompare(b.parent_dms_nr || b.dms_nr, undefined, { numeric: true, sensitivity: "base" });
      }

      return a.parent_dms_nr.localeCompare(b.parent_dms_nr || "", undefined, { numeric: true, sensitivity: "base" }) || 0;
    });
  });

  appointment.car_check_events?.sort((a, b) => b.id - a.id);
  appointment.customer_communication?.check_in_results?.sort((a, b) => a.id - b.id);
  appointment.customer_communication?.events?.sort((event1, event2) => event1.created_on.localeCompare(event2.created_on) || event1.id - event2.id);
  appointment.desk_communications?.sort((comm1, comm2) => comm2.created_on.localeCompare(comm1.created_on));
  appointment.kiosk_communications?.sort((comm1, comm2) => comm2.created_on.localeCompare(comm1.created_on));
  appointment.status_history?.sort((a, b) => a.created_on.localeCompare(b.created_on));

  return { ...appointment, car: appointment.car ? groupTruckTyres(appointment.car) : undefined };
};

const formatAppointment = (app: Appointment) => {
  const appointment = sortAppointmentProperties(updateAppointmentStatusIdentifier(app));
  if (appointment.car)
    appointment.car.tyres = appointment.car.tyres?.filter(t => [TYRE_LOCATIONS.OnVehicle, TYRE_LOCATIONS.InStorage].includes(t.location) || t.replacements?.length) ?? [];

  return appointment;
};

const getRequestPayload = (urlParams: AppointmentDetailUrlParams, appointmentId?: number): AppointmentDetailRequestPayload => {
  const { id: urlId, locationId, woNr } = urlParams;
  const id = appointmentId || Number(urlId);

  return id ? { id } : { dealer_location_id: Number(locationId), wo: woNr };
};

export const useRealTimeAppointmentQuery = (id?: number, enabled: boolean = true) => {
  const urlParams = useParams<AppointmentDetailUrlParams>();

  const [appointmentId, setAppointmentId] = useState<number>(0);
  const listeners = useAppointmentListeners(appointmentId, AppointmentDetailsKeys.view);

  const fetchAppointment = async () => {
    const appointment = await getAppointment(AppointmentDetailsKeys.view, getRequestPayload(urlParams, id));

    setAppointmentId(appointment.id);
    return appointment;
  };

  const realTimeQuery = useRealTimeTypedQuery({
    queryKey: AppointmentDetailsKeys.view,
    queryFn: fetchAppointment,
    select: formatAppointment,
    listeners,
    enabled
  });

  return {
    appointment: realTimeQuery.data,
    loading: realTimeQuery.isFetching,
    error: realTimeQuery.error
  };
};
